import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { BarChart3, Download } from 'lucide-react';
import { Card, CardHeader } from '@/components/ui/Card';
import { Alert } from '@/components/ui/Alert';
import { EmptyState } from '@/components/ui/EmptyState';
import { Tabs } from '@/components/ui/Tabs';
import { Button } from '@/components/ui/Button';
import { ToggleRow } from '@/components/ui/ToggleRow';
import { Select } from '@/components/ui/Select';
import { SaveBar } from '@/components/ui/SaveBar';
import { Skeleton } from '@/components/ui/Skeleton';
import { useGuildConfig } from '@/hooks/useGuildConfig';

/** Matches cogs/analytics.py's RETENTION_CHOICES exactly — the Bridge
 * rejects any other value with a 422 (bridge/config_registry.py). */
const RETENTION_CHOICES = [7, 30, 90, 180, 365] as const;

interface AnalyticsData {
  enabled: boolean;
  retention_days: number;
  track_bots: boolean;
}

const tabs = [
  { id: 'overview', label: 'Overview' },
  { id: 'members', label: 'Members' },
  { id: 'activity', label: 'Activity' },
  { id: 'moderation', label: 'Moderation' },
];

export function AnalyticsPage() {
  const { guildId } = useParams<{ guildId: string }>();
  const { state, current, dirty, saving, saveError, resetting, update, save, discard, reset } =
    useGuildConfig<AnalyticsData>(guildId, 'analytics');
  const [tab, setTab] = useState('overview');

  return (
    <div className="max-w-3xl space-y-6 pb-24">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-h1 text-text-primary mb-1">Analytics</h1>
          <p className="text-small text-text-secondary">Members, activity, channels, voice, moderation, and engagement.</p>
        </div>
        <Button variant="secondary" size="sm" disabled>
          <Download size={14} aria-hidden="true" />
          Export CSV
        </Button>
      </div>

      {saveError && <Alert tone="danger" title="Couldn't save" description={saveError} />}

      {state.kind === 'loading' && <Card><Skeleton className="h-24 w-full" /></Card>}
      {state.kind === 'forbidden' && (
        <Alert tone="danger" title="You don't have permission to view this" description="Analytics settings require Manage Server." />
      )}
      {state.kind === 'unavailable' && (
        <Alert tone="danger" title="Couldn't load analytics settings" description={state.message} />
      )}

      {current && (
        <Card>
          <CardHeader title="Settings" />
          <div className="divide-y divide-border-subtle">
            <ToggleRow title="Collect analytics" checked={current.enabled} onChange={(v) => update({ enabled: v })} />
            <ToggleRow title="Track bot accounts" description="Off by default — most servers only want human activity." checked={current.track_bots} onChange={(v) => update({ track_bots: v })} />
            <div className="py-3 flex items-center justify-between gap-4">
              <div>
                <p className="text-body-medium text-text-primary">Data retention</p>
                <p className="text-small text-text-secondary">How long raw data is kept before aggregation.</p>
              </div>
              <div className="w-32">
                <Select value={current.retention_days} onChange={(e) => update({ retention_days: Number(e.target.value) })}>
                  {RETENTION_CHOICES.map((d) => (
                    <option key={d} value={d}>{d} days</option>
                  ))}
                </Select>
              </div>
            </div>
          </div>
          <SaveBar visible={dirty || saving} onSave={save} onDiscard={discard} saving={saving} />
          {!dirty && (
            <button type="button" onClick={reset} disabled={resetting} className="mt-3 text-small text-text-tertiary hover:text-text-secondary underline underline-offset-2 disabled:opacity-50">
              {resetting ? 'Resetting…' : 'Reset to defaults'}
            </button>
          )}
        </Card>
      )}

      <Alert
        tone="info"
        title="Charts aren't connected yet"
        description="This part is entirely read-only and reuses the bot's own existing CSV export code path once connected — out of scope for this pass. The settings above are live."
      />

      <Tabs tabs={tabs} active={tab} onChange={setTab} />

      <Card>
        <EmptyState
          icon={BarChart3}
          title="No data to show yet"
          description="Charts render here once connected. The bot never stores message content or attachments for analytics — only aggregate counts."
        />
      </Card>
    </div>
  );
}
